import { useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { createPortal } from 'react-dom'
import { Plus, Key, TrendingUp, X, Book, BarChart3, Star, Upload, Edit, Trash2, Eye, Download, Code, FileText, Rocket } from 'lucide-react'
import { developerAPI } from '../services/api'
import { useModalClose } from '../hooks/useModalClose'
import DashboardHeader from '../components/DashboardHeader'
import DashboardFooter from '../components/DashboardFooter'
import toast from 'react-hot-toast'

export default function DeveloperPortal() {
  const [activeTab, setActiveTab] = useState<'overview' | 'apps' | 'docs' | 'api-keys' | 'analytics'>('overview')
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState<any>(null)
  const [showCreateApiKeyModal, setShowCreateApiKeyModal] = useState(false)
  const [newApiKey, setNewApiKey] = useState('')
  const [selectedAppForAnalytics, setSelectedAppForAnalytics] = useState<any>(null)
  const [newApp, setNewApp] = useState({
    name: '',
    slug: '',
    description: '',
    category: 'productivity',
    version: '1.0.0',
    icon_url: '',
    callback_url: '',
    webhook_url: '',
    repository_url: '',
    package_url: '',
    manifest: '',
    settings_schema: '',
  })
  const [updateData, setUpdateData] = useState({
    version: '',
    changelog: '',
    manifest: '',
  })
  const queryClient = useQueryClient()

  const { data: myApps } = useQuery({
    queryKey: ['developer-apps'],
    queryFn: async () => {
      const { data } = await developerAPI.getMyApps()
      console.log('Developer apps response:', data)
      // Ensure we always return an array
      return Array.isArray(data) ? data : []
    },
  })

  const { data: apiKeys } = useQuery({
    queryKey: ['developer-api-keys'],
    queryFn: async () => {
      const { data } = await developerAPI.getApiKeys()
      // Ensure we always return an array
      return Array.isArray(data) ? data : []
    },
    enabled: activeTab === 'api-keys',
  })

  const { data: analytics } = useQuery({
    queryKey: ['developer-analytics'],
    queryFn: async () => {
      const { data } = await developerAPI.getAnalytics()
      return data
    },
    enabled: activeTab === 'analytics',
  })

  const handleSubmitApp = async () => {
    if (!newApp.name || !newApp.slug || !newApp.description) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      await developerAPI.submitApp(newApp)
      queryClient.invalidateQueries({ queryKey: ['developer-apps'] })
      toast.success('App submitted for review!')
      setShowSubmitModal(false)
      setNewApp({
        name: '',
        slug: '',
        description: '',
        category: 'productivity',
        version: '1.0.0',
        icon_url: '',
        callback_url: '',
        webhook_url: '',
        repository_url: '',
        package_url: '',
        manifest: '',
        settings_schema: '',
      })
    } catch (error) {
      toast.error('Failed to submit app')
    }
  }

  const handleUpdateApp = async () => {
    if (!updateData.version || !updateData.changelog) {
      toast.error('Please fill in version and changelog')
      return
    }

    try {
      await developerAPI.updateApp(showUpdateModal.id, updateData)
      queryClient.invalidateQueries({ queryKey: ['developer-apps'] })
      toast.success('App update submitted!')
      setShowUpdateModal(null)
      setUpdateData({ version: '', changelog: '', manifest: '' })
    } catch (error) {
      toast.error('Failed to update app')
    }
  }

  const deleteAppMutation = useMutation({
    mutationFn: (id: number) => developerAPI.deleteApp(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer-apps'] })
      toast.success('App deleted')
    },
    onError: () => toast.error('Failed to delete app'),
  })

  const createApiKeyMutation = useMutation({
    mutationFn: (data: { name: string; app_id?: number }) => developerAPI.createApiKey(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['developer-api-keys'] })
      setNewApiKey(response.data.key)
      toast.success('API key created! Save it now - you won\'t see it again.')
    },
    onError: () => toast.error('Failed to create API key'),
  })

  const revokeApiKeyMutation = useMutation({
    mutationFn: (keyId: number) => developerAPI.revokeApiKey(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer-api-keys'] })
      toast.success('API key revoked successfully')
    },
    onError: () => toast.error('Failed to revoke API key'),
  })

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Rocket },
    { id: 'apps', label: 'My Apps', icon: Code },
    { id: 'docs', label: 'Documentation', icon: Book },
    { id: 'api-keys', label: 'API Keys', icon: Key },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  ]

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <DashboardHeader />

      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Developer Portal</h1>
            <p className="text-gray-600 mt-2">Build and manage apps for Dashit.io</p>
          </div>
          {activeTab === 'apps' && (
            <button 
              onClick={() => setShowSubmitModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
            >
              <Plus size={20} />
              Submit New App
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <h3 className="text-gray-600 font-medium mb-2">Total Apps</h3>
                <div className="text-3xl font-bold text-gray-900">{myApps?.length || 0}</div>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <h3 className="text-gray-600 font-medium mb-2">Approved Apps</h3>
                <div className="text-3xl font-bold text-green-600">
                  {myApps?.filter((app: any) => app.status === 'approved').length || 0}
                </div>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <h3 className="text-gray-600 font-medium mb-2">Total Installs</h3>
                <div className="text-3xl font-bold text-blue-600">
                  {myApps?.reduce((sum: number, app: any) => sum + (app.install_count || 0), 0) || 0}
                </div>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <h3 className="text-gray-600 font-medium mb-2">Avg Rating</h3>
                <div className="flex items-center gap-2">
                  <div className="text-3xl font-bold text-yellow-600">
                    {myApps && myApps.length > 0
                      ? (myApps.reduce((sum: number, app: any) => sum + (app.avg_rating || 0), 0) / myApps.length).toFixed(1)
                      : '0.0'}
                  </div>
                  <Star size={24} className="text-yellow-500 fill-yellow-500" />
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setActiveTab('apps')}
                  className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition text-left"
                >
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Code className="text-primary-600" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Manage Apps</div>
                    <div className="text-sm text-gray-600">View and update your apps</div>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('docs')}
                  className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition text-left"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Book className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Read Documentation</div>
                    <div className="text-sm text-gray-600">Learn how to build apps</div>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('api-keys')}
                  className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition text-left"
                >
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Key className="text-green-600" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">API Keys</div>
                    <div className="text-sm text-gray-600">Manage your API keys</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Recent Apps */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Apps</h2>
              {myApps && myApps.length > 0 ? (
                <div className="space-y-3">
                  {myApps.slice(0, 5).map((app: any) => (
                    <div key={app.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-xl">📦</span>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{app.name}</div>
                          <div className="text-sm text-gray-600">v{app.version || '1.0.0'}</div>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        app.status === 'approved' ? 'bg-green-100 text-green-800' :
                        app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {app.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">No apps yet. Submit your first app to get started!</p>
              )}
            </div>
          </div>
        )}

        {/* Apps Tab */}
        {activeTab === 'apps' && (
          <div className="space-y-6">
            {myApps && myApps.length > 0 ? (
              myApps.map((app: any) => (
                <div key={app.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                        <span className="text-3xl">📦</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-xl text-gray-900">{app.name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            app.status === 'approved' ? 'bg-green-100 text-green-800' :
                            app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {app.status}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-2">{app.description}</p>
                        <div className="flex gap-4 text-sm text-gray-500">
                          <span>Version: {app.version || '1.0.0'}</span>
                          <span>•</span>
                          <span>Category: {app.category}</span>
                          <span>•</span>
                          <span>Installs: {app.install_count || 0}</span>
                          <span>•</span>
                          <div className="flex items-center gap-1">
                            <Star size={14} className="text-yellow-500 fill-yellow-500" />
                            <span>{app.avg_rating ? app.avg_rating.toFixed(1) : '0.0'}</span>
                            <span>({app.review_count || 0} reviews)</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedAppForAnalytics(app)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        title="View Analytics"
                      >
                        <BarChart3 size={20} />
                      </button>
                      <button
                        onClick={() => {
                          setShowUpdateModal(app)
                          setUpdateData({
                            version: app.version || '1.0.0',
                            changelog: '',
                            manifest: app.manifest || '',
                          })
                        }}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                        title="Update Version"
                      >
                        <Upload size={20} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this app?')) {
                            deleteAppMutation.mutate(app.id)
                          }
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Delete App"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white p-12 rounded-xl shadow-sm border border-gray-200 text-center">
                <Code size={48} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Apps Yet</h3>
                <p className="text-gray-600 mb-4">Get started by submitting your first app!</p>
                <button
                  onClick={() => setShowSubmitModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                >
                  <Plus size={20} />
                  Submit App
                </button>
              </div>
            )}
          </div>
        )}

        {/* Documentation Tab */}
        {activeTab === 'docs' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">API Documentation</h2>
              <p className="text-gray-600 mt-1">Complete guide to building apps for Dashit.io</p>
            </div>
            <div className="p-6">
              <DocumentationViewer />
            </div>
          </div>
        )}

        {/* API Keys Tab */}
        {activeTab === 'api-keys' && (
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">API Keys</h2>
                <p className="text-gray-600 mt-1">Manage your API keys for accessing Dashit.io APIs</p>
              </div>
              <button
                onClick={() => setShowCreateApiKeyModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
              >
                <Plus size={18} />
                Create New Key
              </button>
            </div>
            {apiKeys && apiKeys.length > 0 ? (
              <div className="space-y-3 mt-6">
                {apiKeys.map((key: any) => (
                  <div key={key.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <div className="font-mono text-sm text-gray-900 mb-1">{key.key_prefix}...</div>
                      <div className="text-xs text-gray-500">
                        {key.name && <span className="font-medium">{key.name} • </span>}
                        Created: {new Date(key.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        if (confirm(`Are you sure you want to revoke this API key?\n\n${key.key_prefix}...\n\nThis action cannot be undone and any integrations using this key will stop working immediately.`)) {
                          revokeApiKeyMutation.mutate(key.id)
                        }
                      }}
                      disabled={revokeApiKeyMutation.isPending}
                      className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                    >
                      {revokeApiKeyMutation.isPending ? 'Revoking...' : 'Revoke'}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Key size={48} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium mb-2">No API keys yet</p>
                <p className="text-sm">Create your first API key to get started with our APIs</p>
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Developer Analytics</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Total Installs</div>
                  <div className="text-3xl font-bold text-gray-900">
                    {myApps?.reduce((sum: number, app: any) => sum + (app.install_count || 0), 0) || 0}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Average Rating</div>
                  <div className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                    {myApps && myApps.length > 0
                      ? (myApps.reduce((sum: number, app: any) => sum + (app.avg_rating || 0), 0) / myApps.length).toFixed(1)
                      : '0.0'}
                    <Star size={24} className="text-yellow-500 fill-yellow-500" />
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Total Reviews</div>
                  <div className="text-3xl font-bold text-gray-900">
                    {myApps?.reduce((sum: number, app: any) => sum + (app.review_count || 0), 0) || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Submit App Modal */}
      {showSubmitModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Submit New App</h2>
              <button onClick={() => setShowSubmitModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">App Name *</label>
                <input
                  type="text"
                  value={newApp.name}
                  onChange={(e) => setNewApp({ ...newApp, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="My Awesome App"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Slug *</label>
                <input
                  type="text"
                  value={newApp.slug}
                  onChange={(e) => setNewApp({ ...newApp, slug: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="my-awesome-app"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Version *</label>
                <input
                  type="text"
                  value={newApp.version}
                  onChange={(e) => setNewApp({ ...newApp, version: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="1.0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea
                  value={newApp.description}
                  onChange={(e) => setNewApp({ ...newApp, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  placeholder="Describe your app..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={newApp.category}
                  onChange={(e) => setNewApp({ ...newApp, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="productivity">Productivity</option>
                  <option value="entertainment">Entertainment</option>
                  <option value="utilities">Utilities</option>
                  <option value="social">Social</option>
                  <option value="news">News</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Widget Delivery Section */}
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Widget Code & Integration</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Repository URL <span className="text-gray-500 font-normal">(GitHub, GitLab, etc.)</span>
                    </label>
                    <input
                      type="url"
                      value={newApp.repository_url}
                      onChange={(e) => setNewApp({ ...newApp, repository_url: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="https://github.com/username/widget-repo"
                    />
                    <p className="text-xs text-gray-500 mt-1">Link to your widget's source code repository</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Package URL <span className="text-gray-500 font-normal">(NPM, CDN, or hosted bundle)</span>
                    </label>
                    <input
                      type="url"
                      value={newApp.package_url}
                      onChange={(e) => setNewApp({ ...newApp, package_url: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="https://cdn.example.com/my-widget.js or npm:package-name"
                    />
                    <p className="text-xs text-gray-500 mt-1">Where we can load your widget code from</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Callback URL <span className="text-gray-500 font-normal">(Optional - for OAuth flows)</span>
                    </label>
                    <input
                      type="url"
                      value={newApp.callback_url}
                      onChange={(e) => setNewApp({ ...newApp, callback_url: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="https://your-api.com/callback"
                    />
                    <p className="text-xs text-gray-500 mt-1">For widgets that need OAuth authentication</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Webhook URL <span className="text-gray-500 font-normal">(Optional - for real-time updates)</span>
                    </label>
                    <input
                      type="url"
                      value={newApp.webhook_url}
                      onChange={(e) => setNewApp({ ...newApp, webhook_url: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="https://your-api.com/webhook"
                    />
                    <p className="text-xs text-gray-500 mt-1">For receiving events and notifications</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Icon URL <span className="text-gray-500 font-normal">(Recommended)</span>
                    </label>
                    <input
                      type="url"
                      value={newApp.icon_url}
                      onChange={(e) => setNewApp({ ...newApp, icon_url: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="https://cdn.example.com/icon.png"
                    />
                    <p className="text-xs text-gray-500 mt-1">256x256px PNG recommended</p>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900 font-medium mb-2">📝 Note: Widget Code Delivery</p>
                  <p className="text-xs text-blue-800">
                    Currently, Dashit.io doesn't have a code sandbox system implemented. 
                    These fields are for documentation purposes. A full implementation would need:
                  </p>
                  <ul className="text-xs text-blue-800 mt-2 ml-4 list-disc space-y-1">
                    <li>Secure code sandbox for executing widget code</li>
                    <li>Dynamic module loading (ESM/CommonJS)</li>
                    <li>Resource limits (CPU, memory, network)</li>
                    <li>Security isolation between widgets</li>
                  </ul>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSubmitApp}
                  className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium"
                >
                  Submit App
                </button>
                <button
                  onClick={() => setShowSubmitModal(false)}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Update App Modal */}
      {showUpdateModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Update App: {showUpdateModal.name}</h2>
              <button onClick={() => setShowUpdateModal(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Version *</label>
                <input
                  type="text"
                  value={updateData.version}
                  onChange={(e) => setUpdateData({ ...updateData, version: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="1.1.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Changelog *</label>
                <textarea
                  value={updateData.changelog}
                  onChange={(e) => setUpdateData({ ...updateData, changelog: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows={4}
                  placeholder="What's new in this version..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleUpdateApp}
                  className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium"
                >
                  Submit Update
                </button>
                <button
                  onClick={() => setShowUpdateModal(null)}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* App Analytics Modal */}
      {selectedAppForAnalytics && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Analytics: {selectedAppForAnalytics.name}</h2>
              <button onClick={() => setSelectedAppForAnalytics(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Total Installs</div>
                  <div className="text-3xl font-bold text-gray-900">{selectedAppForAnalytics.install_count || 0}</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Average Rating</div>
                  <div className="flex items-center gap-2">
                    <div className="text-3xl font-bold text-gray-900">{selectedAppForAnalytics.avg_rating?.toFixed(1) || '0.0'}</div>
                    <Star size={24} className="text-yellow-500 fill-yellow-500" />
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Total Reviews</div>
                  <div className="text-3xl font-bold text-gray-900">{selectedAppForAnalytics.review_count || 0}</div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">Recent Reviews</h3>
                <p className="text-gray-600 text-sm">Review system coming soon...</p>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create API Key Modal */}
      {showCreateApiKeyModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Create API Key</h2>
              <button
                onClick={() => {
                  setShowCreateApiKeyModal(false)
                  setNewApiKey('')
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              {newApiKey ? (
                <div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-yellow-800 font-medium mb-2">⚠️ Save this key now!</p>
                    <p className="text-xs text-yellow-700">You won't be able to see it again. Store it securely.</p>
                  </div>
                  <div className="bg-gray-900 p-4 rounded-lg mb-4">
                    <code className="text-sm text-green-400 break-all">{newApiKey}</code>
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(newApiKey)
                      toast.success('Copied to clipboard!')
                    }}
                    className="w-full px-4 py-2 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition mb-2"
                  >
                    Copy to Clipboard
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateApiKeyModal(false)
                      setNewApiKey('')
                    }}
                    className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={(e) => {
                  e.preventDefault()
                  const formData = new FormData(e.currentTarget)
                  createApiKeyMutation.mutate({
                    name: formData.get('name') as string,
                  })
                }}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Key Name (optional)
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder="e.g., Production API Key"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Give your API key a memorable name</p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowCreateApiKeyModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createApiKeyMutation.isPending}
                      className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
                    >
                      {createApiKeyMutation.isPending ? 'Creating...' : 'Create Key'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Footer */}
      <DashboardFooter />
    </div>
  )
}

// Documentation Viewer Component
function DocumentationViewer() {
  const [activeSection, setActiveSection] = useState('getting-started')

  const sections = [
    { id: 'getting-started', title: 'Getting Started', icon: Rocket },
    { id: 'authentication', title: 'Authentication', icon: Key },
    { id: 'widgets', title: 'Widget API', icon: Code },
    { id: 'examples', title: 'Code Examples', icon: FileText },
  ]

  return (
    <div className="flex gap-6">
      {/* Sidebar */}
      <div className="w-64 flex-shrink-0">
        <nav className="space-y-1">
          {sections.map(section => {
            const Icon = section.icon
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition ${
                  activeSection === section.id
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon size={18} />
                {section.title}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 max-w-none docs-content">
        {activeSection === 'getting-started' && (
          <div>
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Getting Started</h2>
            <p className="text-gray-700 mb-6">Welcome to the HomeDashboard Developer Platform! This guide will help you build and publish your first app.</p>
            
            <h3 className="text-2xl font-semibold mb-3 mt-8 text-gray-900">Prerequisites</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li>Developer account (contact admin to upgrade)</li>
              <li>Basic knowledge of JavaScript and React</li>
              <li>Understanding of REST APIs</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-8 text-gray-900">Your First App</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-2 text-gray-700">
              <li>Create an API key in the API Keys tab</li>
              <li>Design your widget interface</li>
              <li>Use our Widget API to store and retrieve data</li>
              <li>Submit your app for review</li>
              <li>Once approved, users can install it!</li>
            </ol>

            <h3 className="text-2xl font-semibold mb-3 mt-8 text-gray-900">App Structure</h3>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
{`{
  "name": "My App",
  "slug": "my-app",
  "version": "1.0.0",
  "description": "A great widget",
  "category": "productivity",
  "manifest": {
    "permissions": ["user.read", "widget.write"],
    "settings": {
      "apiKey": { "type": "string", "required": true }
    }
  }
}`}
            </pre>
          </div>
        )}

        {activeSection === 'authentication' && (
          <div>
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Authentication</h2>
            <p className="text-gray-700 mb-6">All API requests require authentication using an API key.</p>

            <h3 className="text-2xl font-semibold mb-3 mt-8 text-gray-900">Creating an API Key</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-2 text-gray-700">
              <li>Go to the API Keys tab</li>
              <li>Click "Create New Key"</li>
              <li>Save the key securely (you won't see it again)</li>
            </ol>

            <h3 className="text-2xl font-semibold mb-3 mt-8 text-gray-900">Using Your API Key</h3>
            <p className="text-gray-700 mb-4">Include your API key in the <code className="bg-gray-100 px-2 py-1 rounded text-sm text-red-600">X-API-Key</code> header:</p>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
{`fetch('https://api.homedashboard.com/api/v1/widget/data', {
  headers: {
    'X-API-Key': 'hd_your_api_key_here',
    'Content-Type': 'application/json'
  }
})`}
            </pre>
          </div>
        )}

        {activeSection === 'widgets' && (
          <div>
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Widget API</h2>
            <p className="text-gray-700 mb-6">The Widget API allows you to store and retrieve data for your widgets.</p>

            <h3 className="text-2xl font-semibold mb-3 mt-8 text-gray-900">Store Data</h3>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
{`POST /api/v1/widget/data

{
  "widget_id": "123",
  "key": "user_preferences",
  "value": { "theme": "dark", "language": "en" }
}`}
            </pre>

            <h3 className="text-2xl font-semibold mb-3 mt-8 text-gray-900">Retrieve Data</h3>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
{`GET /api/v1/widget/data?widget_id=123&key=user_preferences`}
            </pre>
          </div>
        )}

        {activeSection === 'examples' && (
          <div>
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Code Examples</h2>
            
            <h3 className="text-2xl font-semibold mb-3 mt-8 text-gray-900">React Widget Example</h3>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
{`import { useState, useEffect } from 'react'

export default function MyWidget({ widget }) {
  const [data, setData] = useState(null)

  useEffect(() => {
    // Fetch widget data
    fetch(\`/api/widget/\${widget.id}/data\`)
      .then(res => res.json())
      .then(setData)
  }, [widget.id])

  return (
    <div className="p-4">
      <h3>{widget.name}</h3>
      {data && <pre>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  )
}`}
            </pre>

            <h3 className="text-2xl font-semibold mb-3 mt-8 text-gray-900">Python Integration Example</h3>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
{`import requests

API_KEY = "hd_your_api_key"
BASE_URL = "https://api.homedashboard.com/api/v1"

headers = {
    "X-API-Key": API_KEY,
    "Content-Type": "application/json"
}

# Store data
response = requests.post(
    f"{BASE_URL}/widget/data",
    headers=headers,
    json={
        "widget_id": "123",
        "key": "counter",
        "value": 42
    }
)

print(response.json())`}
            </pre>

            <h3 className="text-2xl font-semibold mb-3 mt-8 text-gray-900">PHP Integration Example</h3>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
{`<?php

$apiKey = "hd_your_api_key";
$baseUrl = "https://api.homedashboard.com/api/v1";

// Initialize cURL
$ch = curl_init();

// Set headers
$headers = [
    "X-API-Key: " . $apiKey,
    "Content-Type: application/json"
];

// Store data
$data = [
    "widget_id" => "123",
    "key" => "counter",
    "value" => 42
];

curl_setopt_array($ch, [
    CURLOPT_URL => $baseUrl . "/widget/data",
    CURLOPT_POST => true,
    CURLOPT_POSTFIELDS => json_encode($data),
    CURLOPT_HTTPHEADER => $headers,
    CURLOPT_RETURNTRANSFER => true
]);

$response = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);

if ($httpCode === 200) {
    $result = json_decode($response, true);
    echo "Success: " . print_r($result, true);
} else {
    echo "Error: HTTP $httpCode - $response";
}

curl_close($ch);

// Retrieve data
$ch = curl_init();
$url = $baseUrl . "/widget/data?widget_id=123&key=counter";

curl_setopt_array($ch, [
    CURLOPT_URL => $url,
    CURLOPT_HTTPHEADER => $headers,
    CURLOPT_RETURNTRANSFER => true
]);

$response = curl_exec($ch);
$data = json_decode($response, true);

echo "Retrieved value: " . $data['value'];
curl_close($ch);`}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}
